import React, { useContext, useEffect, useMemo, useState } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { List, Modal, Skeleton, Tabs, TabsProps } from "antd"
import { formatMultipleDropoffs, formatToPickup } from "@/utils/stopsFormatter"
import { GenerateNewRouteFunc, IRoute } from "@/hooks/useCalculateRoute"
import { defaultModalsState, dispatchPageModalsAtom } from "@/atoms/dispatchPageModalsAtom"
import OrderDetailsModalTitle from "./components/OrderDetailsModalTitle"
import OrderDetailsSummary from "./components/OrderDetailsSummary"
import DeliveryProgressBar from "@/modules/active-orders/OrderTable/components/DeliveryProgressBar"
import { useDelivery } from "@/hooks/useDelivery"
import OrderEventHistoryTable from "./components/OrderEventHistoryTable"
import { useTranslation } from "react-i18next"
import { useInterval } from "usehooks-ts"
import { getSingleOrderSelector } from "@/atoms/ordersAtom"
import { stopsAtom } from "@/atoms/stopsAtom"
import { DestinationStatuses, OrderType, StopType } from "@/types/orders.types"
import { OrderStatusesTypes } from "@/types/statuses.types"
import { isPayingUserSelector, isSupportRoleSelector } from "@/atoms/userAtom"
import { ServerDeliveryType } from "@/types/deliveryTypes.types"
import { ProvidersType } from "@/types/providers.types"
import DestinationStatusTag from "@/modules/active-orders/shared/DestinationStatusTag"
import OrderStatusTag from "@/modules/shared/Statuses/OrderStatusTag"
import { KosmoMapContext } from "@/modules/shared/Map/MapProvider"
import { globalMapAtom } from "@/atoms/globalMapAtom"

interface OrderDetailsModalProps {
  generateNewRoute: GenerateNewRouteFunc
  multipleRoutes: IRoute[]
  removeAllRoutes: () => void
  isLoadingRoute?: boolean
}

enum OrderDetailsModalTab {
  MAP = "map",
  HISTORY = "history",
}

const EXECUTION_INTERVAL = 20 * 1000 // 20 seconds

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  generateNewRoute,
  multipleRoutes,
  removeAllRoutes,
  isLoadingRoute,
}) => {
  const { t } = useTranslation()
  const mapContext = useContext(KosmoMapContext)
  const isSupportRole = useRecoilValue(isSupportRoleSelector)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const setMapGlobalState = useSetRecoilState(globalMapAtom)
  const [{ orderDetailsModal }, setDispatchPageModals] = useRecoilState(dispatchPageModalsAtom)
  const { stops } = useRecoilValue(stopsAtom)

  const { orderDetailsModalId, stopIndex } = orderDetailsModal

  const selectedStop = stops?.find(
    (stop) => stop.id === orderDetailsModal?.orderDetailsModalId && stop.stopIndex === stopIndex
  )

  const transitionOrder = useRecoilValue(getSingleOrderSelector(orderDetailsModalId))
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState<OrderDetailsModalTab>(OrderDetailsModalTab.MAP)
  const {
    loading: deliveryLoading,
    triggerGetDelivery,
    pickupPODs,
    dropoffPODs,
    eventHistory,
    order,
    setOrder,
    updateSingleOrderStopStatus,
    updateSingleOrderStatus,
    isStatusLoading,
  } = useDelivery()

  // Memoizing the array avoids the map to detect a change in the array passed
  const currentRoute = useMemo(() => {
    const routes = multipleRoutes?.filter((route) =>
      orderDetailsModalId ? route?.id === orderDetailsModalId : false
    )
    return routes.length > 0 && (stopIndex === null || stopIndex === undefined) ? routes : undefined
  }, [orderDetailsModalId, multipleRoutes, stopIndex])

  const currentOrder = order || transitionOrder

  const recipientMarker = {
    lat: order?.recipients?.[stopIndex || 0]?.location?.latitude,
    lng: order?.recipients?.[stopIndex || 0]?.location?.longitude,
  }
  const endMarker = {
    lat: order?.metadata?.endLatitude || 0,
    lng: order?.metadata?.endLongitude || 0,
  }

  const handleCancel = () => {
    removeAllRoutes()
    setIsModalVisible(false)
    setDispatchPageModals(defaultModalsState)
    setOrder(undefined)
  }

  useEffect(() => {
    if (orderDetailsModalId) {
      setActiveTab(OrderDetailsModalTab.MAP)
      setIsModalVisible(true)
      triggerGetDelivery(orderDetailsModalId)
    }
    if (!orderDetailsModalId) {
      setIsModalVisible(false)
    }
  }, [orderDetailsModalId])

  useEffect(() => {
    if (!isModalVisible || !order) {
      return
    }
    // We wait single order details with the route payload to avoid calling Google with the transition order
    if (!deliveryLoading) {
      const dropoffs = formatMultipleDropoffs(order?.recipients, undefined, true)
      const destinationPoints = dropoffs.slice()
      if (order.arrival !== undefined) {
        destinationPoints.push({
          name: order.arrival.contact.name,
          phone: order.arrival.contact.phone,
          latitude: order.arrival.location.latitude,
          longitude: order.arrival.location.longitude,
          address: order.arrival.location.address,
          type: StopType.ARRIVAL,
        })
      }
      const newStopsOrder = [
        [order?.sender?.location?.latitude || 0, order?.sender?.location?.longitude || 0],
        ...destinationPoints?.map((destination) => [
          destination?.latitude || 0,
          destination?.longitude || 0,
        ]),
      ]

      generateNewRoute(order.id, formatToPickup(order?.sender), destinationPoints, {
        routes: order?.routeDirections?.routes,
        stops: order?.routeDirections?.stops || [],
        type: OrderType.ACTIVE,
        newStopsOrder,
      })
    }
  }, [
    isModalVisible,
    order?.recipients,
    order?.sender,
    deliveryLoading,
    order?.routeDirections?.stops,
  ])

  useInterval(
    () => {
      if (orderDetailsModalId) {
        triggerGetDelivery(orderDetailsModalId)
      }
    },
    isModalVisible && orderDetailsModalId ? EXECUTION_INTERVAL : null
  )

  const tabItems: TabsProps["items"] = [
    {
      key: OrderDetailsModalTab.MAP,
      label: t`common.map`,
    },
    {
      key: OrderDetailsModalTab.HISTORY,
      label: t`common.history`,
    },
  ]

  // TODO: Refactor types to be able to use recipient from order within stop
  const stopWithOrderRecipient = selectedStop
    ? {
        ...selectedStop,
        recipient:
          stopIndex !== undefined && order?.recipients?.[stopIndex]
            ? order?.recipients?.[stopIndex]!
            : selectedStop?.recipient!,
      }
    : undefined

  let mapProgressBarStopStatus

  switch (stopWithOrderRecipient?.recipient?.status) {
    case DestinationStatuses.CANCELED:
      mapProgressBarStopStatus = OrderStatusesTypes.CANCELLED
      break
    case DestinationStatuses.DELIVERED:
      mapProgressBarStopStatus = OrderStatusesTypes.DELIVERED
      break
    case DestinationStatuses.RETURNING:
      mapProgressBarStopStatus = OrderStatusesTypes.RETURNING
      break
    case DestinationStatuses.TO_DROPOFF:
      mapProgressBarStopStatus = OrderStatusesTypes.TO_DROPOFF
      break
    case DestinationStatuses.RETURNED:
      mapProgressBarStopStatus = OrderStatusesTypes.RETURNED
      break
    default:
      mapProgressBarStopStatus = OrderStatusesTypes.CREATED
      break
  }

  const isCancelled = !!stopWithOrderRecipient
    ? [DestinationStatuses.CANCELED].includes(stopWithOrderRecipient?.recipient?.status!)
    : !!order?.status && [OrderStatusesTypes.CANCELLED].includes(order?.status)

  const isCancellable =
    !!stopWithOrderRecipient && !isCancelled
      ? true
      : !!order?.status &&
        [OrderStatusesTypes.CREATED, OrderStatusesTypes.SUBMITTED].includes(order?.status) &&
        order?.provider !== ProvidersType.IFOOD

  const isOrderAgainDisabled =
    !stopWithOrderRecipient &&
    !!order?.deliveryType &&
    [ServerDeliveryType.NEXT_DAY, ServerDeliveryType.NEXT_THREE_DAYS].includes(order?.deliveryType)

  useEffect(() => {
    if (!mapContext?.KosmoMap || !orderDetailsModal?.orderDetailsModalId) {
      return
    }
    if (activeTab === OrderDetailsModalTab.MAP) {
      setMapGlobalState(() => ({
        current: {
          from: "order",
          isMulti: order && order?.recipients?.length > 0,
          ...(stopIndex !== undefined
            ? {
                originMapMarker: {
                  lat: order?.sender?.location?.latitude || 0,
                  lng: order?.sender?.location?.longitude || 0,
                },
                destinationMapMarker: {
                  lat: recipientMarker?.lat || 0,
                  lng: recipientMarker?.lng || 0,
                },
                endMapMarker: endMarker,
              }
            : { routes: currentRoute, endMapMarker: endMarker }),
          driver: order?.driver,
          strictSetBounds: true,
        },
        prev: null,
      }))
    }
  }, [order?.sender, recipientMarker, order?.driver, mapContext?.KosmoMap, endMarker])

  return (
    <>
      <Modal
        title={
          currentOrder && (
            <OrderDetailsModalTitle
              order={currentOrder}
              title={
                stopWithOrderRecipient?.recipient?.orderReferenceId
                  ? stopWithOrderRecipient?.recipient?.orderReferenceId
                  : currentOrder?.orderID
              }
              cancelStopCallback={async () => {
                if (currentOrder?.id) {
                  triggerGetDelivery(currentOrder?.id)
                }
              }}
              selectedStop={stopWithOrderRecipient}
              isCancellable={isCancellable}
              isCancelled={isCancelled}
              isOrderAgainDisabled={isOrderAgainDisabled}
              isViewOnly={isSupportRole || !isPayingUser}
              statusTag={
                stopWithOrderRecipient ? (
                  <DestinationStatusTag
                    destinationStatus={
                      stopWithOrderRecipient?.recipient?.status || DestinationStatuses.EMPTY
                    }
                  />
                ) : (
                  <OrderStatusTag status={currentOrder?.status} />
                )
              }
            />
          )
        }
        destroyOnClose={false}
        open={isModalVisible}
        centered
        closable={true}
        styles={{ body: { height: "80vh", paddingRight: "0px" } }}
        footer={false}
        onCancel={handleCancel}
        width={"90%"}
      >
        <div className="flex h-full mt-8">
          <div className="flex flex-col overflow-y-auto w-1/2 px-4">
            {currentOrder && isModalVisible ? (
              <OrderDetailsSummary
                order={currentOrder}
                selectedStop={stopWithOrderRecipient}
                pickupPODs={pickupPODs}
                dropoffPODs={dropoffPODs}
                key={orderDetailsModalId}
                isLoading={isStatusLoading}
                triggerGetDelivery={async () => {
                  if (currentOrder?.id) {
                    await triggerGetDelivery(currentOrder?.id)
                  }
                }}
                updateSingleOrderStopStatus={async (
                  orderId: string,
                  newStatus: DestinationStatuses,
                  index: number
                ) => {
                  await updateSingleOrderStopStatus(orderId, newStatus, index)
                  await triggerGetDelivery(orderId)
                }}
                updateOrderStatus={updateSingleOrderStatus}
              />
            ) : (
              <div className="flex flex-col mt-4 gap-y-4">
                {[0, 1, 2, 3, 4, 5].map((_, index) => (
                  <Skeleton loading={true} active key={index}>
                    <List.Item.Meta
                      title={<a href={"item.href"}>{"item.title"}</a>}
                      description={"item.description"}
                    />
                    {"item.content"}
                  </Skeleton>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col overflow-y-auto w-1/2 px-4">
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={(key) => {
                setActiveTab(key as OrderDetailsModalTab)
              }}
              destroyInactiveTabPane={false}
            />
            <div className="flex flex-1">
              <div
                className={`w-full h-full ${activeTab !== OrderDetailsModalTab.MAP && "hidden"}`}
              >
                <div className="h-[70%] relative" id="dispatch-map">
                  {/* Only one instance of the map can be loaded at a time - in conflict with main order event history */}
                  {!!mapContext?.KosmoMap && <mapContext.KosmoMap />}
                  {!!mapContext?.KosmoMap && isLoadingRoute && (
                    <div className="absolute top-0 left-0 right-0 bottom-0 z-30 bg-white translate-right-opacity" />
                  )}
                </div>
                <div className="flex h-[30%] items-center">
                  {currentOrder ? (
                    <>
                      {stopWithOrderRecipient ? (
                        <DeliveryProgressBar
                          createdAt={currentOrder?.createdAt}
                          {...(stopWithOrderRecipient?.recipient?.status ===
                          DestinationStatuses.CANCELED
                            ? {
                                cancelledAt: stopWithOrderRecipient?.recipient?.dropoffEta,
                              }
                            : {})}
                          deliveredAt={stopWithOrderRecipient?.recipient?.dropoffEta}
                          status={mapProgressBarStopStatus}
                        />
                      ) : (
                        <DeliveryProgressBar
                          status={currentOrder?.status}
                          createdAt={currentOrder?.createdAt}
                          toPickup={currentOrder?.driverAssignedAt}
                          toDropoff={currentOrder?.pickupAt || currentOrder?.pickupEndEta}
                          deliveredAt={
                            currentOrder?.dropoffAt ||
                            currentOrder?.arrival?.plannedArrivalAt ||
                            currentOrder?.deliveryEta
                          }
                          cancelledAt={currentOrder?.cancelledAt}
                        />
                      )}
                    </>
                  ) : (
                    <Skeleton loading={true} active key={0}>
                      <List.Item.Meta
                        title={<a href={"item.href"}>{"item.title"}</a>}
                        description={"item.description"}
                      />
                      {"item.content"}
                    </Skeleton>
                  )}
                </div>
              </div>
              <div className={`h-full ${activeTab !== OrderDetailsModalTab.HISTORY && "hidden"}`}>
                <OrderEventHistoryTable
                  loading={deliveryLoading}
                  history={
                    eventHistory?.filter((event) => {
                      return stopIndex === undefined
                        ? true
                        : event?.stopIndex === stopIndex ||
                            (!event?.stopIndex && !event?.recipientFullName)
                    }) ?? []
                  }
                  order={currentOrder}
                  isFocused={activeTab === OrderDetailsModalTab.HISTORY}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default OrderDetailsModal
